import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { useIsMounted } from "@swa-ui/browser";
import { AriaLive } from "@swa-ui/core";
import { swaDate } from "@swa-ui/date";

/**
 * Countdown is a component that will display a simple timer that counts down to zero.
 */
export const Countdown = (props) => {
  const {
    ariaLabelCountingDown,
    ariaLabelDone,
    ariaLiveDone,
    ariaLiveRestarted,
    ariaLiveWarning,
    durationInSeconds,
    onDone,
    paused,
    restartKey,
    warningIntervalInSeconds,
  } = props;
  const [secondsUntilDone, setSecondsUntilDone] = useState(durationInSeconds);
  const [restarted, setRestarted] = useState(false);
  const timerId = useRef(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    if (secondsUntilDone > 0) {
      timerId.current = setTimeout(() => {
        setSecondsUntilDone(secondsUntilDone - (paused ? 0 : 1));
      }, 1000);
    }

    return () => clearTimeout(timerId.current);
  }, [secondsUntilDone, paused]);

  useEffect(() => {
    if (secondsUntilDone <= 0) {
      onDone();
    } else if (secondsUntilDone <= durationInSeconds - warningIntervalInSeconds) {
      setRestarted(false);
    }
  }, [secondsUntilDone]);

  useEffect(() => {
    if (isMounted) {
      setSecondsUntilDone(durationInSeconds);
      setRestarted(true);
    }
  }, [restartKey]);

  return (
    <>
      <span aria-hidden>(</span>
      <span
        role="timer"
        aria-label={
          secondsUntilDone > 0
            ? renderText(ariaLabelCountingDown, {
                seconds: secondsUntilDone,
              })
            : renderText(ariaLabelDone)
        }
      >
        {swaDate.duration(secondsUntilDone, "seconds").format("m:ss")}
      </span>
      <span aria-hidden>)</span>
      {renderAriaLive()}
    </>
  );

  function renderAriaLive() {
    let ariaLiveText = "";

    if (secondsUntilDone <= 0) {
      ariaLiveText = renderText(ariaLiveDone);
    } else if (restarted) {
      ariaLiveText = renderText(ariaLiveRestarted);
    } else {
      ariaLiveText = renderText(ariaLiveWarning, {
        seconds: Math.ceil(secondsUntilDone / warningIntervalInSeconds) * warningIntervalInSeconds,
      });
    }

    return <AriaLive>{ariaLiveText}</AriaLive>;
  }

  function renderText(text, data) {
    return typeof text === "function" ? text(data) : text;
  }
};

Countdown.propTypes = {
  /**
   * The aria label to use when the countdown is counting down.
   */
  ariaLabelCountingDown: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The aria label to use when the countdown is done.
   */
  ariaLabelDone: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The aria live region text to use when the countdown is done.
   */
  ariaLiveDone: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The aria live region text to use when the countdown is restarted.
   */
  ariaLiveRestarted: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The aria live region text to use when the countdown is nearing completion.
   */
  ariaLiveWarning: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The duration in seconds for the countdown.
   */
  durationInSeconds: PropTypes.number.isRequired,

  /**
   * The callback to invoke when the countdown is done.
   */
  onDone: PropTypes.func.isRequired,

  /**
   * Indicates if the countdown is paused.
   */
  paused: PropTypes.bool,

  /**
   * A key to restart the countdown.
   */
  restartKey: PropTypes.any,

  /**
   * The interval in seconds to show the countdown warning message.
   */
  warningIntervalInSeconds: PropTypes.number,
};
